// WHAT: Oura Token Status Hook
// WHY:  Checks if Oura token is available in secure storage
// DOMAIN: Health → Oura
// DEPENDENCIES: Oura token service
// RISKS:
//   - NOTE: Hook only checks on mount (doesn't update if token changes)
//   - NOTE: Status is binary ("geladen" or "fehlt") - no detailed error info
//   - NOTE: Hardcoded German status strings - not internationalized

import { useEffect, useState } from "react";

import { loadOuraToken } from "@/domains/health/oura/services";

type LoadOuraTokenFunction = () => Promise<string>;
const safeLoadOuraToken: LoadOuraTokenFunction = loadOuraToken as LoadOuraTokenFunction;

/**
 * WHAT:
 *   Custom hook for checking Oura token availability.
 *
 * WHY:
 *   Provides simple token status check for conditional rendering in UI.
 *
 * @returns Token status string ("geladen" | "fehlt")
 *
 * BEHAVIOR:
 *   - Checks token on mount by calling loadOuraToken service
 *   - Sets status to "geladen" if token exists, "fehlt" if not
 *   - Status doesn't update after mount (static check)
 *
 * RISKS:
 *   - NOTE: Status only checked once on mount (doesn't reflect token changes)
 *   - NOTE: No error details if token load fails for other reasons
 *   - NOTE: Hardcoded German strings - not internationalized
 *
 */
export const useOuraTokenStatus = () => {
  const [tokenStatus, setTokenStatus] = useState<"fehlt" | "geladen">("geladen");

  useEffect(() => {
    const loadToken = async (): Promise<void> => {
      try {
        const token = await safeLoadOuraToken();
        if (token) {
          setTokenStatus("geladen");
        } else {
          setTokenStatus("fehlt");
        }
      } catch {
        setTokenStatus("fehlt");
      }
    };
    void loadToken();
  }, []);

  return tokenStatus;
};
